import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";
import { 
  IManholeProject, 
  IPipeConnection, 
  ICatalogProduct, 
  IStackComponent,
  IBillOfMaterialsEntry,
  IDrawingObject,
  IManholeInternals
} from "../../../../types/manhole.types";

export class ManholeViewStore {
  // Observables - UI state only
  activeProjectId: string | null = null;
  selectedPipeId: string | null = null;
  currentView: 'side' | 'top' = 'side';
  zoomLevel: number = 1;
  panPosition: { x: number, y: number } = { x: 0, y: 0 };
  isLoading: boolean = false;
  error: string | null = null;
  
  // Form state for new/edit project
  editingProject: Partial<IManholeProject> | null = null;
  isDirty: boolean = false;
  
  // Dialog form data
  dialogFormData: Record<string, any> = {};

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  // Actions - UI state management
  setActiveProject = (id: string | null) => {
    this.activeProjectId = id;
    this.selectedPipeId = null;
    this.editingProject = null;
    this.isDirty = false;
  };

  setSelectedPipe = (id: string | null) => {
    this.selectedPipeId = id;
  };

  setCurrentView = (view: 'side' | 'top') => {
    this.currentView = view;
  };

  updateZoom = (level: number) => {
    this.zoomLevel = Math.max(0.1, Math.min(5, level));
  };

  updatePan = (x: number, y: number) => {
    this.panPosition = { x, y };
  };

  resetView = () => {
    this.zoomLevel = 1;
    this.panPosition = { x: 0, y: 0 };
  };

  setLoading = (loading: boolean) => {
    this.isLoading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  // Project editing actions
  startEditingProject = (project?: IManholeProject, accountId?: string | number) => {
    if (project) {
      this.editingProject = { ...project };
    } else {
      // New project defaults for estimating phase (no job yet)
      this.editingProject = {
        accountId: accountId || '',  // Required: must select account
        jobId: undefined,  // Optional: only set after award
        customerName: '',  // Will be populated from account
        customerContact: '',
        jobName: '',  // Empty until awarded
        jobNumber: '',  // Empty until awarded
        structureId: '',
        station: '',
        type: 'Sanitary',
        structureType: 'Sanitary',
        city: '',
        state: '',
        salesperson: '',
        revision: 'A',
        status: 'draft',  // Start as draft estimate
        manholeStructure: {
          rimElevation: 0,
          pipes: [],
          internals: {
            hasSteps: false,
            hasLadder: false,
            stepAngleDegrees: 180,
            stepType: 'standard'
          }
        }
      };
    }
    this.isDirty = false;
  };

  updateEditingProject = (updates: Partial<IManholeProject>) => {
    if (!this.editingProject) return;
    this.editingProject = { ...this.editingProject, ...updates };
    this.isDirty = true;
  };
  
  // Dialog form data management
  setDialogFormData = (data: Record<string, any>) => {
    this.dialogFormData = data;
  };
  
  updateDialogFormData = (updates: Record<string, any>) => {
    this.dialogFormData = { ...this.dialogFormData, ...updates };
  };
  
  clearDialogFormData = () => {
    this.dialogFormData = {};
  };

  cancelEditing = () => {
    this.editingProject = null;
    this.isDirty = false;
  };

  // Pipe management actions
  addPipe = async () => {
    if (!this.editingProject?.manholeStructure) return;
    
    const newPipe: IPipeConnection = {
      manholeProjectId: this.activeProjectId || undefined,
      pipeNumber: this.editingProject.manholeStructure.pipes.length + 1,
      invertElevation: 0,
      pipeDiameterInches: 8,
      pipeMaterial: 'PVC 3034',
      angleDegrees: 0,
      holeType: '40612AW',
      holeSizeInches: 12
    };
    
    // If we have an active project saved, also create in backend
    if (this.activeProjectId && (this.rootStore as any).manholePipeConnectionsStore) {
      try {
        await (this.rootStore as any).manholePipeConnectionsStore.create(newPipe);
      } catch (error) {
        console.error('Failed to create pipe in backend:', error);
      }
    }
    
    this.editingProject.manholeStructure.pipes.push(newPipe);
    this.isDirty = true;
  };

  updatePipe = async (pipeId: string, updates: Partial<IPipeConnection>) => {
    if (!this.editingProject?.manholeStructure) return;
    
    const pipeIndex = this.editingProject.manholeStructure.pipes.findIndex(p => p.id === pipeId);
    if (pipeIndex !== -1) {
      // Update in backend if pipe has an ID
      if (pipeId && (this.rootStore as any).manholePipeConnectionsStore) {
        try {
          await (this.rootStore as any).manholePipeConnectionsStore.patch(pipeId, updates);
        } catch (error) {
          console.error('Failed to update pipe in backend:', error);
        }
      }
      
      this.editingProject.manholeStructure.pipes[pipeIndex] = {
        ...this.editingProject.manholeStructure.pipes[pipeIndex],
        ...updates
      };
      this.isDirty = true;
    }
  };

  removePipe = async (pipeId: string) => {
    if (!this.editingProject?.manholeStructure) return;
    
    // Remove from backend if pipe has an ID
    if (pipeId && (this.rootStore as any).manholePipeConnectionsStore) {
      try {
        await (this.rootStore as any).manholePipeConnectionsStore.remove(pipeId);
      } catch (error) {
        console.error('Failed to remove pipe from backend:', error);
      }
    }
    
    this.editingProject.manholeStructure.pipes = 
      this.editingProject.manholeStructure.pipes.filter(p => p.id !== pipeId);
    
    // Renumber pipes
    this.editingProject.manholeStructure.pipes.forEach((pipe, index) => {
      pipe.pipeNumber = index + 1;
    });
    
    this.isDirty = true;
  };

  // Component stack management
  addComponent = (product: ICatalogProduct) => {
    if (!this.editingProject?.manholeStructure) return;
    
    if (!this.editingProject.manholeStructure.components) {
      this.editingProject.manholeStructure.components = [];
    }
    
    const components = this.editingProject.manholeStructure.components;
    const newComponent: IStackComponent = {
      instanceId: `comp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      product,
      positionInStack: components.length,
      bottomElevation: this.calculateBottomElevation(components.length)
    };
    
    components.push(newComponent);
    this.isDirty = true;
  };

  removeComponent = (instanceId: string) => {
    if (!this.editingProject?.manholeStructure?.components) return;
    
    const components = this.editingProject.manholeStructure.components;
    const index = components.findIndex(c => c.instanceId === instanceId);
    if (index === -1) return;
    
    components.splice(index, 1);
    
    // Recalculate positions and elevations
    this.recalculateComponentPositions();
    this.isDirty = true;
  };

  moveComponent = (instanceId: string, direction: 'up' | 'down') => {
    if (!this.editingProject?.manholeStructure?.components) return;
    
    const components = this.editingProject.manholeStructure.components;
    const index = components.findIndex(c => c.instanceId === instanceId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= components.length) return;
    
    // Swap components
    [components[index], components[newIndex]] = [components[newIndex], components[index]];
    
    // Recalculate positions and elevations
    this.recalculateComponentPositions();
    this.isDirty = true;
  };

  private recalculateComponentPositions = () => {
    if (!this.editingProject?.manholeStructure?.components) return;
    
    const components = this.editingProject.manholeStructure.components;
    let currentElevation = this.lowestInvert || 90; // Start from lowest point
    
    components.forEach((component, index) => {
      component.positionInStack = index;
      component.bottomElevation = currentElevation;
      currentElevation += (component.product.heightInches || 0) / 12; // Convert inches to feet
    });
  };

  private calculateBottomElevation = (position: number): number => {
    if (!this.editingProject?.manholeStructure?.components) {
      return this.lowestInvert || 90;
    }
    
    const components = this.editingProject.manholeStructure.components;
    let elevation = this.lowestInvert || 90;
    
    for (let i = 0; i < position && i < components.length; i++) {
      elevation += (components[i].product.heightInches || 0) / 12;
    }
    
    return elevation;
  };

  // Internal components management
  updateInternals = (updates: Partial<IManholeInternals>) => {
    if (!this.editingProject?.manholeStructure) return;
    
    if (!this.editingProject.manholeStructure.internals) {
      this.editingProject.manholeStructure.internals = {
        hasSteps: false,
        hasLadder: false,
        stepAngleDegrees: 180,
        stepType: 'standard'
      };
    }
    
    this.editingProject.manholeStructure.internals = {
      ...this.editingProject.manholeStructure.internals,
      ...updates
    };
    
    this.isDirty = true;
  };

  // Convert estimate to awarded project
  awardProject = async (jobId: string | number) => {
    if (!this.activeProject) return;
    
    this.setLoading(true);
    this.setError(null);
    
    try {
      const updates = {
        jobId,
        status: 'awarded' as const,
        awardedDate: new Date()
      };
      
      const id = this.activeProject.id || this.activeProject.projectId;
      await (this.rootStore as any).manholeProjectsStore.patch(id, updates);
      
      // Reload to get denormalized job data
      await this.loadProject(id!);
    } catch (error: any) {
      runInAction(() => {
        this.setError(error.message || 'Failed to award project');
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  // Create quote from current project
  createQuote = async () => {
    if (!this.activeProject) return null;
    
    this.setLoading(true);
    
    try {
      const quote = {
        accountId: this.activeProject.accountId,
        manholeProjectId: this.activeProject.id || this.activeProject.projectId,
        quoteNumber: `Q-${Date.now()}`,  // Should be generated by backend
        items: this.billOfMaterials,
        subtotal: this.totalCost,
        tax: this.totalCost * 0.08,  // 8% tax example
        total: this.totalCost * 1.08,
        status: 'draft' as const,
        validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)  // 30 days
      };
      
      const result = await (this.rootStore as any).manholeQuotesStore?.create(quote);
      
      // Update project status to quoted
      if (result) {
        const projectId = this.activeProject.id || this.activeProject.projectId;
        await (this.rootStore as any).manholeProjectsStore.patch(projectId, {
          quoteNumber: result.quoteNumber,
          status: 'quoted'
        });
      }
      
      return result;
    } catch (error: any) {
      console.error('Failed to create quote:', error);
      this.setError('Failed to create quote');
      return null;
    } finally {
      this.setLoading(false);
    }
  };

  // Async actions
  saveProject = async () => {
    if (!this.editingProject) return;
    
    this.setLoading(true);
    this.setError(null);
    
    try {
      // Prepare data for backend - flatten manholeStructure if it exists
      const projectData = { ...this.editingProject };
      
      // If manholeStructure exists, extract rimElevation to top level
      if (projectData.manholeStructure) {
        if (projectData.manholeStructure.rimElevation !== undefined) {
          projectData.rimElevation = projectData.manholeStructure.rimElevation;
        }
        // Remove the nested structure since backend doesn't have this column
        delete projectData.manholeStructure;
      }
      
      // Ensure we're using the form data for new projects
      if (!projectData.id && !projectData.projectId && this.dialogFormData) {
        Object.assign(projectData, this.dialogFormData);
      }
      
      if (this.editingProject.id || this.editingProject.projectId) {
        // Update existing project
        const id = this.editingProject.id || this.editingProject.projectId;
        await (this.rootStore as any).manholeProjectsStore.patch(id, projectData);
      } else {
        // Create new project
        const result = await (this.rootStore as any).manholeProjectsStore.create(projectData);
        if (result) {
          this.activeProjectId = result.id || result.projectId;
        }
      }
      
      runInAction(() => {
        this.editingProject = null;
        this.isDirty = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.setError(error.message || 'Failed to save project');
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  loadProject = async (projectId: string) => {
    this.setLoading(true);
    this.setError(null);
    
    try {
      await (this.rootStore as any).manholeProjectsStore.get(projectId);
      runInAction(() => {
        this.activeProjectId = projectId;
      });
    } catch (error: any) {
      runInAction(() => {
        this.setError(error.message || 'Failed to load project');
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  deleteProject = async (projectId: string) => {
    this.setLoading(true);
    this.setError(null);
    
    try {
      await (this.rootStore as any).manholeProjectsStore.remove(projectId);
      runInAction(() => {
        if (this.activeProjectId === projectId) {
          this.activeProjectId = null;
        }
      });
    } catch (error: any) {
      runInAction(() => {
        this.setError(error.message || 'Failed to delete project');
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  // Calculator service integration with response transformation
  calculateManholeOnServer = async () => {
    if (!this.activeProjectId) return null;
    
    this.setLoading(true);
    try {
      // Call backend calculator service
      const backendResponse = await (this.rootStore as any).manholeCalculatorStore?.create({
        manholeProjectId: this.activeProjectId
      });
      
      if (!backendResponse) {
        throw new Error('No response from calculator service');
      }
      
      // Transform backend response to match frontend expectations
      const transformedResponse = this.transformCalculatorResponse(backendResponse);
      
      // Optionally save calculated components to manhole-components service
      if (transformedResponse.components && (this.rootStore as any).manholeComponentsStore) {
        for (const component of transformedResponse.components) {
          await (this.rootStore as any).manholeComponentsStore.create({
            ...component,
            manholeProjectId: this.activeProjectId
          });
        }
      }
      
      return transformedResponse;
    } catch (error: any) {
      console.error('Server calculation failed:', error);
      this.setError('Failed to calculate manhole on server');
      return null;
    } finally {
      this.setLoading(false);
    }
  };

  // Transform backend calculator response to frontend format
  private transformCalculatorResponse(backendResponse: any) {
    const components = backendResponse.components || [];
    
    // Calculate bottom elevations for each component
    let currentElevation = this.activeProject?.manholeStructure?.rimElevation || 0;
    const transformedComponents = components.map((product: any, index: number) => {
      const bottomElevation = currentElevation - (product.heightInches / 12);
      currentElevation = bottomElevation;
      
      return {
        instanceId: `comp-${Date.now()}-${index}`,
        product: this.normalizeProduct(product),
        positionInManhole: index,
        positionInStack: index, // Keep both for compatibility
        bottomElevation
      };
    });
    
    // Generate bill of materials
    const bomMap = new Map<string, any>();
    transformedComponents.forEach((comp: any) => {
      const key = comp.product.productId || comp.product.productCode;
      if (bomMap.has(key)) {
        bomMap.get(key).quantity += 1;
      } else {
        bomMap.set(key, {
          item: key,
          description: comp.product.description,
          quantity: 1,
          weight: comp.product.weightLbs || comp.product.weight || 0,
          unitCost: comp.product.costUSD || comp.product.basePrice || 0,
          totalCost: comp.product.costUSD || comp.product.basePrice || 0
        });
      }
    });
    
    const billOfMaterials = Array.from(bomMap.values());
    const totalWeight = billOfMaterials.reduce((sum, item) => sum + (item.weight * item.quantity), 0);
    const totalCost = backendResponse.totalCost || billOfMaterials.reduce((sum, item) => sum + item.totalCost, 0);
    
    return {
      success: backendResponse.success,
      components: transformedComponents,
      billOfMaterials,
      totalWeight,
      totalCost,
      totalHeight: backendResponse.totalHeight
    };
  }

  // Normalize product fields from backend
  private normalizeProduct(product: any): ICatalogProduct {
    return {
      ...product,
      id: product.id || product.productId,
      productId: product.productId || product.productCode,
      type: product.type || product.manholeType,
      weightLbs: product.weightLbs || product.weight,
      costUSD: product.costUSD || product.basePrice
    };
  };

  // Test data creation for development
  createTestPipes = () => {
    if (!this.editingProject?.manholeStructure) {
      console.warn('No editing project to add test pipes to');
      return;
    }

    // Clear existing pipes
    this.editingProject.manholeStructure.pipes = [];

    // Create test pipes at different angles
    const testPipes: Partial<IPipeConnection>[] = [
      { 
        pipeNumber: 1, 
        invertElevation: 95.5, 
        pipeDiameterInches: 18, 
        angleDegrees: 0, // North
        direction: 'inlet',
        pipeMaterial: 'RCP'
      },
      { 
        pipeNumber: 2, 
        invertElevation: 94.8, 
        pipeDiameterInches: 24, 
        angleDegrees: 90, // East
        direction: 'outlet',
        pipeMaterial: 'RCP'
      },
      { 
        pipeNumber: 3, 
        invertElevation: 95.2, 
        pipeDiameterInches: 12, 
        angleDegrees: 180, // South
        direction: 'inlet',
        pipeMaterial: 'PVC'
      },
      { 
        pipeNumber: 4, 
        invertElevation: 95.0, 
        pipeDiameterInches: 15, 
        angleDegrees: 270, // West
        direction: 'inlet',
        pipeMaterial: 'HDPE'
      },
      { 
        pipeNumber: 5, 
        invertElevation: 94.5, 
        pipeDiameterInches: 8, 
        angleDegrees: 45, // Northeast
        direction: 'inlet',
        pipeMaterial: 'PVC'
      }
    ];

    testPipes.forEach(pipeData => {
      const fullPipe: IPipeConnection = {
        manholeProjectId: this.activeProjectId || undefined,
        pipeNumber: pipeData.pipeNumber || 1,
        invertElevation: pipeData.invertElevation || 0,
        pipeDiameterInches: pipeData.pipeDiameterInches || 8,
        pipeMaterial: pipeData.pipeMaterial || 'PVC',
        angleDegrees: pipeData.angleDegrees || 0,
        direction: pipeData.direction || 'inlet',
        holeType: '40612AW',
        holeSizeInches: (pipeData.pipeDiameterInches || 8) + 4
      };
      this.editingProject.manholeStructure!.pipes.push(fullPipe);
    });

    this.isDirty = true;
    console.log('Test pipes created:', this.editingProject.manholeStructure.pipes);
  };

  // Generate Bill of Materials
  generateBOM = (): IBillOfMaterialsEntry[] => {
    const bom: IBillOfMaterialsEntry[] = [];
    
    // Add components to BOM
    if (this.editingProject?.manholeStructure?.components) {
      this.editingProject.manholeStructure.components.forEach(comp => {
        bom.push({
          item: comp.product.productCode || comp.product.productId || 'COMP',
          description: comp.product.description,
          quantity: 1,
          weight: comp.product.weightLbs || 0,
          unitCost: comp.product.costUSD || 0,
          totalCost: comp.product.costUSD || 0
        });
      });
    }
    
    // Add pipes to BOM
    if (this.editingProject?.manholeStructure?.pipes) {
      this.editingProject.manholeStructure.pipes.forEach(pipe => {
        bom.push({
          item: `PIPE-${pipe.pipeDiameterInches}`,
          description: `${pipe.pipeDiameterInches}" ${pipe.pipeMaterial} Pipe Connection`,
          quantity: 1,
          weight: pipe.pipeDiameterInches * 10, // Estimated
          unitCost: pipe.pipeDiameterInches * 25, // Estimated
          totalCost: pipe.pipeDiameterInches * 25
        });
      });
    }
    
    // Add internals to BOM
    if (this.editingProject?.manholeStructure?.internals) {
      const internals = this.editingProject.manholeStructure.internals;
      if (internals.hasSteps) {
        bom.push({
          item: 'STEPS-STD',
          description: `Standard Steps at ${internals.stepAngleDegrees}°`,
          quantity: Math.floor((this.requiredPrecastHeight || 10) / 1.25), // One step per 1.25 feet
          weight: 15,
          unitCost: 45,
          totalCost: 45 * Math.floor((this.requiredPrecastHeight || 10) / 1.25)
        });
      }
      if (internals.hasLadder) {
        bom.push({
          item: 'LADDER-FX',
          description: 'Fixed Ladder System',
          quantity: 1,
          weight: 150,
          unitCost: 850,
          totalCost: 850
        });
      }
    }
    
    return bom;
  };
  
  // Export BOM to CSV
  exportBOMToCSV = () => {
    const bom = this.generateBOM();
    if (bom.length === 0) {
      console.warn('No items in BOM to export');
      return;
    }
    
    // Create CSV content
    const headers = ['Item', 'Description', 'Quantity', 'Weight (lbs)', 'Unit Cost', 'Total Cost'];
    const rows = bom.map(item => [
      item.item,
      item.description,
      item.quantity,
      item.weight,
      `$${item.unitCost.toFixed(2)}`,
      `$${item.totalCost.toFixed(2)}`
    ]);
    
    // Add totals row
    const totalWeight = bom.reduce((sum, item) => sum + item.weight * item.quantity, 0);
    const totalCost = bom.reduce((sum, item) => sum + item.totalCost, 0);
    rows.push(['', 'TOTAL', '', totalWeight, '', `$${totalCost.toFixed(2)}`]);
    
    // Convert to CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `manhole-bom-${this.editingProject?.structureId || 'export'}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('BOM exported successfully');
  };

  // Test data creation for component stack
  createTestComponents = () => {
    if (!this.editingProject?.manholeStructure) {
      console.warn('No editing project to add test components to');
      return;
    }

    // Clear existing components
    this.editingProject.manholeStructure.components = [];

    // Create typical manhole stack
    const testComponents: ICatalogProduct[] = [
      {
        id: 'base-48',
        productId: 'MH-BASE-48',
        name: 'Base Section',
        description: '48" Manhole Base with Monolithic Base',
        type: 'base',
        heightInches: 36,
        diameterInches: 48,
        wallThicknessInches: 5,
        weightLbs: 4500,
        costUSD: 850
      },
      {
        id: 'riser-48-24',
        productId: 'MH-RISER-48-24',
        name: 'Riser Section',
        description: '48" x 24" Riser Section',
        type: 'riser',
        heightInches: 24,
        diameterInches: 48,
        wallThicknessInches: 5,
        weightLbs: 2000,
        costUSD: 425
      },
      {
        id: 'riser-48-12',
        productId: 'MH-RISER-48-12',
        name: 'Riser Section',
        description: '48" x 12" Riser Section',
        type: 'riser',
        heightInches: 12,
        diameterInches: 48,
        wallThicknessInches: 5,
        weightLbs: 1000,
        costUSD: 225
      },
      {
        id: 'cone-48-24',
        productId: 'MH-CONE-48-24',
        name: 'Cone Section',
        description: '48" to 24" Eccentric Cone',
        type: 'cone',
        heightInches: 36,
        diameterInches: 48,
        wallThicknessInches: 5,
        weightLbs: 2500,
        costUSD: 550
      },
      {
        id: 'ring-24-6',
        productId: 'MH-RING-24-6',
        name: 'Grade Ring',
        description: '24" x 6" Grade Ring',
        type: 'ring',
        heightInches: 6,
        diameterInches: 24,
        wallThicknessInches: 3,
        weightLbs: 200,
        costUSD: 75
      },
      {
        id: 'frame-cover',
        productId: 'MH-FC-STD',
        name: 'Frame & Cover',
        description: 'Standard Cast Iron Frame & Cover',
        type: 'frame',
        heightInches: 4,
        diameterInches: 24,
        wallThicknessInches: 2,
        weightLbs: 350,
        costUSD: 425
      }
    ];

    // Add each component
    testComponents.forEach(product => {
      this.addComponent(product);
    });

    console.log('Test components created:', this.editingProject.manholeStructure.components);
  };

  // Computed values - Reference ServiceStore entities
  get activeProject(): IManholeProject | null {
    if (!this.activeProjectId) return null;
    
    const project = (this.rootStore as any).manholeProjectsStore?.entities?.find(
      (p: any) => p.id === this.activeProjectId || p.projectId === this.activeProjectId
    );
    
    if (!project) return null;
    
    // If project doesn't have embedded pipes, fetch them from separate service
    if (!project.manholeStructure?.pipes) {
      const pipes = this.projectPipes;
      return {
        ...project,
        manholeStructure: {
          rimElevation: project.rimElevation || 0,
          pipes: pipes
        }
      };
    }
    
    return project;
  }

  get allProjects(): IManholeProject[] {
    return (this.rootStore as any).manholeProjectsStore?.entities || [];
  }

  // Get estimates only (not awarded)
  get estimates(): IManholeProject[] {
    return this.allProjects.filter(p => 
      p.status === 'draft' || p.status === 'estimate' || p.status === 'quoted'
    );
  }

  // Get awarded projects only
  get awardedProjects(): IManholeProject[] {
    return this.allProjects.filter(p => 
      p.status === 'awarded' || p.status === 'revised' || p.status === 'completed'
    );
  }

  // Get all accounts for dropdown
  get accounts() {
    return (this.rootStore as any).accountsStore?.entities || [];
  }

  // Get pipes from separate service
  get projectPipes(): IPipeConnection[] {
    if (!this.activeProjectId) return [];
    
    return (this.rootStore as any).manholePipeConnectionsStore?.entities?.filter(
      (pipe: any) => pipe.manholeProjectId === this.activeProjectId
    ) || [];
  }

  get catalogProducts(): ICatalogProduct[] {
    // First check manhole-components service
    let products = (this.rootStore as any).manholeComponentsStore?.entities || [];
    
    // If empty, fall back to products service with filter
    if (products.length === 0) {
      products = (this.rootStore as any).productsStore?.entities?.filter(
        (p: any) => p.isManholeProduct === true
      ) || [];
    }
    
    // Normalize field names for consistent access
    return products.map((p: any) => ({
      ...p,
      type: p.type || p.manholeType,
      weightLbs: p.weightLbs || p.weight,
      costUSD: p.costUSD || p.basePrice,
      productId: p.productId || p.productCode
    }));
  }

  get bases(): ICatalogProduct[] {
    return this.catalogProducts.filter(p => {
      const productType = p.type || p.manholeType;
      return productType === 'base';
    });
  }

  get risers(): ICatalogProduct[] {
    return this.catalogProducts.filter(p => {
      const productType = p.type || p.manholeType;
      return productType === 'riser';
    })
      .sort((a, b) => b.heightInches - a.heightInches); // Sort largest first for optimization
  }

  get cones(): ICatalogProduct[] {
    return this.catalogProducts.filter(p => p.type === 'cone');
  }

  get slabs(): ICatalogProduct[] {
    return this.catalogProducts.filter(p => p.type === 'slab');
  }

  get frames(): ICatalogProduct[] {
    return this.catalogProducts.filter(p => p.type === 'frame');
  }

  get covers(): ICatalogProduct[] {
    return this.catalogProducts.filter(p => p.type === 'cover');
  }

  get accessories(): ICatalogProduct[] {
    return this.catalogProducts.filter(p => p.type === 'accessory');
  }

  get lowestInvert(): number {
    const project = this.editingProject || this.activeProject;
    if (!project?.manholeStructure?.pipes?.length) return 0;
    
    return Math.min(...project.manholeStructure.pipes.map(p => p.invertElevation));
  }

  get requiredPrecastHeight(): number {
    const project = this.editingProject || this.activeProject;
    if (!project?.manholeStructure) return 0;
    
    const rimElevation = project.manholeStructure.rimElevation;
    const lowestInvert = this.lowestInvert;
    
    // Assume frame and cover height is 6 inches (0.5 feet)
    const frameAndCoverHeight = 0.5;
    
    return Math.max(0, rimElevation - lowestInvert - frameAndCoverHeight);
  }

  get rimToInvert(): number {
    const project = this.editingProject || this.activeProject;
    if (!project?.manholeStructure) return 0;
    
    const rimElevation = project.manholeStructure.rimElevation || project.rimElevation || 0;
    const lowestInvert = this.lowestInvert;
    
    return Math.max(0, rimElevation - lowestInvert);
  }

  get stackComponents(): IStackComponent[] {
    const project = this.editingProject || this.activeProject;
    if (!project?.manholeStructure) return [];
    
    const components: IStackComponent[] = [];
    const requiredHeight = this.requiredPrecastHeight * 12; // Convert to inches
    
    if (requiredHeight <= 0) return components;
    
    // 1. Select base
    const base = this.bases.find(b => b.diameterInches === 48); // Default to 48" diameter
    if (!base) return components;
    
    components.push({
      instanceId: `stack-${Date.now()}-1`,
      product: base,
      positionInStack: 0,
      bottomElevation: this.lowestInvert
    });
    
    let remainingHeight = requiredHeight - base.heightInches;
    let currentElevation = this.lowestInvert + (base.heightInches / 12);
    let position = 1;
    
    // 2. Fill with risers (greedy algorithm - largest first)
    const sortedRisers = [...this.risers];
    
    while (remainingHeight > 24 && sortedRisers.length > 0) { // Leave room for top slab
      const riser = sortedRisers.find(r => r.heightInches <= remainingHeight - 24);
      
      if (!riser) break;
      
      components.push({
        instanceId: `stack-${Date.now()}-${position + 1}`,
        product: riser,
        positionInStack: position,
        bottomElevation: currentElevation
      });
      
      remainingHeight -= riser.heightInches;
      currentElevation += riser.heightInches / 12;
      position++;
    }
    
    // 3. Add top slab or cone
    const topSlab = this.slabs.find(s => s.diameterInches === 48);
    if (topSlab) {
      components.push({
        instanceId: `stack-${Date.now()}-${position + 1}`,
        product: topSlab,
        positionInStack: position,
        bottomElevation: currentElevation
      });
      position++;
      currentElevation += topSlab.heightInches / 12;
    }
    
    // 4. Add frame and cover
    const frame = this.frames[0];
    const cover = this.covers[0];
    
    if (frame) {
      components.push({
        instanceId: `stack-${Date.now()}-${position + 1}`,
        product: frame,
        positionInStack: position,
        bottomElevation: currentElevation
      });
      position++;
    }
    
    if (cover) {
      components.push({
        instanceId: `stack-${Date.now()}-${position + 1}`,
        product: cover,
        positionInStack: position,
        bottomElevation: currentElevation
      });
    }
    
    return components;
  }

  get billOfMaterials(): IBillOfMaterialsEntry[] {
    const components = this.stackComponents;
    const project = this.editingProject || this.activeProject;
    const entries: IBillOfMaterialsEntry[] = [];
    
    // Aggregate components
    const componentMap = new Map<string, { product: ICatalogProduct, quantity: number }>();
    
    components.forEach(comp => {
      const key = comp.product.productId;
      if (componentMap.has(key)) {
        componentMap.get(key)!.quantity++;
      } else {
        componentMap.set(key, { product: comp.product, quantity: 1 });
      }
    });
    
    // Convert to BOM entries
    componentMap.forEach(({ product, quantity }) => {
      entries.push({
        item: product.productId,
        description: product.description,
        quantity,
        weight: product.weightLbs * quantity,
        unitCost: product.costUSD,
        totalCost: product.costUSD * quantity
      });
    });
    
    // Add accessories based on rules
    if (project?.manholeStructure?.pipes?.length) {
      // Add boots for each pipe
      const bootCount = project.manholeStructure.pipes.length;
      entries.push({
        item: 'BOOT-FLEX',
        description: 'Flexible Pipe Boot',
        quantity: bootCount,
        weight: 5 * bootCount,
        unitCost: 45,
        totalCost: 45 * bootCount
      });
    }
    
    // Add sealant for joints (one per component minus 1)
    if (components.length > 1) {
      const jointCount = components.length - 1;
      entries.push({
        item: 'SEAL-MASTIC',
        description: 'Joint Sealant (Roll)',
        quantity: Math.ceil(jointCount / 4), // Assume 4 joints per roll
        weight: 10 * Math.ceil(jointCount / 4),
        unitCost: 120,
        totalCost: 120 * Math.ceil(jointCount / 4)
      });
    }
    
    return entries;
  }

  get totalWeight(): number {
    return this.billOfMaterials.reduce((sum, entry) => sum + entry.weight, 0);
  }

  get totalCost(): number {
    return this.billOfMaterials.reduce((sum, entry) => sum + entry.totalCost, 0);
  }

  get drawingObjects(): IDrawingObject[] {
    const components = this.stackComponents;
    const objects: IDrawingObject[] = [];
    
    if (this.currentView === 'side') {
      // Side view - vertical stack
      components.forEach((comp, index) => {
        const y = comp.bottomElevation * 100; // Scale factor
        const height = comp.product.heightInches * (100 / 12); // Convert to scaled units
        const width = comp.product.diameterInches * (100 / 12);
        
        // Main component rectangle
        objects.push({
          type: 'rect',
          x: -width / 2, // Center horizontally
          y,
          width,
          height,
          fill: this.getComponentColor(comp.product.type),
          stroke: '#333',
          strokeWidth: 1
        });
        
        // Label
        objects.push({
          type: 'text',
          x: width / 2 + 10,
          y: y + height / 2,
          text: comp.product.description,
          fill: '#000'
        });
      });
      
      // Add pipe connections
      const project = this.editingProject || this.activeProject;
      if (project?.manholeStructure?.pipes) {
        project.manholeStructure.pipes.forEach(pipe => {
          const y = pipe.invertElevation * 100;
          const diameter = pipe.pipeDiameterInches * (100 / 12);
          
          objects.push({
            type: 'circle',
            x: -50, // Left side
            y: y + diameter / 2,
            radius: diameter / 2,
            fill: '#4A90E2',
            stroke: '#333',
            strokeWidth: 1
          });
        });
      }
    } else {
      // Top view - circular with pipe holes
      const mainDiameter = 48 * (100 / 12); // 48" manhole
      
      // Main circle
      objects.push({
        type: 'circle',
        x: 0,
        y: 0,
        radius: mainDiameter / 2,
        fill: '#E0E0E0',
        stroke: '#333',
        strokeWidth: 2
      });
      
      // Pipe holes
      const project = this.editingProject || this.activeProject;
      if (project?.manholeStructure?.pipes) {
        project.manholeStructure.pipes.forEach(pipe => {
          const angle = (pipe.angleDegrees * Math.PI) / 180;
          const distance = mainDiameter / 2 - 20; // Offset from edge
          const x = Math.cos(angle) * distance;
          const y = Math.sin(angle) * distance;
          const holeRadius = pipe.holeSizeInches * (100 / 12) / 2;
          
          objects.push({
            type: 'circle',
            x,
            y,
            radius: holeRadius,
            fill: '#4A90E2',
            stroke: '#333',
            strokeWidth: 1
          });
          
          // Pipe number label
          objects.push({
            type: 'text',
            x: x - 5,
            y: y + 5,
            text: `(${pipe.pipeNumber})`,
            fill: '#fff'
          });
        });
      }
    }
    
    return objects;
  }

  private getComponentColor(type: string): string {
    const colors: Record<string, string> = {
      base: '#8B4513',
      riser: '#A0522D',
      cone: '#CD853F',
      slab: '#D2691E',
      ring: '#DEB887',
      frame: '#696969',
      cover: '#2F4F4F',
      accessory: '#778899'
    };
    return colors[type] || '#CCCCCC';
  }

  // Cleanup
  cleanup = () => {
    this.activeProjectId = null;
    this.selectedPipeId = null;
    this.editingProject = null;
    this.isDirty = false;
    this.currentView = 'side';
    this.resetView();
  };
}